import { useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import type { Tenant } from '@/lib/tenant';
import { demoMode } from '@/lib/api';
import { checkOwnerPin, unlockOwner, ownerSignIn } from '@/lib/ownerAuth';

// Вход владельца: отдельный адрес /admin,
// чтобы владелец не попадал в клиентский интерфейс по ошибке.
// Демо: код доступа. Прод: почта и пароль через Pages Functions + D1.
export function OwnerEntry() {
  const { tenant } = useOutletContext<{ tenant: Tenant }>();
  const navigate = useNavigate();

  const [code, setCode] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submitDemo = () => {
    if (checkOwnerPin(code)) {
      unlockOwner();
      navigate('/admin/dashboard');
      return;
    }
    setError('Неверный код. Попробуйте ещё раз.');
  };

  const submitReal = async () => {
    setError(null);
    setBusy(true);
    try {
      await ownerSignIn(email.trim(), password);
      navigate('/admin/dashboard');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось войти.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="mnc-page mnc-owner-entry">
      <section className="mnc-hero mnc-owner-entry-hero">
        <span className="mnc-kicker">Кабинет владельца</span>
        <h1>{tenant.name}</h1>
        <p className="mnc-lead">
          {demoMode
            ? 'Введите код доступа салона, чтобы увидеть записи клиентов.'
            : 'Войдите почтой и паролем, чтобы увидеть записи клиентов.'}
        </p>
      </section>

      {demoMode ? (
        <section className="mnc-step mnc-owner-login-card">
          <label className="mnc-field">
            Код доступа
            <input
              type="password"
              inputMode="numeric"
              autoComplete="off"
              placeholder="••••••••"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submitDemo();
              }}
            />
          </label>
          <button className="mnc-btn mnc-btn-block" disabled={!code} onClick={submitDemo}>
            Войти
          </button>
          {error && <p className="mnc-muted">{error}</p>}
        </section>
      ) : (
        <section className="mnc-step mnc-owner-login-card">
          <label className="mnc-field">
            Почта
            <input
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError(null);
              }}
            />
          </label>
          <label className="mnc-field">
            Пароль
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void submitReal();
              }}
            />
          </label>
          <button
            className="mnc-btn mnc-btn-block"
            disabled={!email || !password || busy}
            onClick={() => void submitReal()}
          >
            {busy ? 'Входим…' : 'Войти'}
          </button>
          {error && <p className="mnc-muted">{error}</p>}
        </section>
      )}

      {demoMode && (
        <p className="mnc-demo">Код по умолчанию: 00000000 — смените его после первого входа.</p>
      )}
    </main>
  );
}
